// ============================================================================
// USERS API - Frontend
// API client para gestión de usuarios
// ============================================================================

import { api } from './client';

// ============================================================================
// TYPES
// ============================================================================

export type Role = 'USER' | 'GESTOR_SEDE' | 'ADMIN';

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: Role;
  isActive: boolean;
  mustChangePass: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  updatedAt: string;
  sitesCount: number;
  sites?: { id: string; code: string; name: string; isDefault: boolean }[];
  tempPassword?: string; // Solo cuando se crea sin contraseña
}

export interface UserSelect {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: Role;
}

export interface CreateUserDto {
  email: string;
  firstName: string;
  lastName: string;
  role: Role;
  password?: string;
  siteIds?: string[];
  defaultSiteId?: string;
}

export interface UpdateUserDto {
  email?: string;
  firstName?: string;
  lastName?: string;
  role?: Role;
}

export interface ChangePasswordDto {
  newPassword: string;
}

export interface UserQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  includeInactive?: boolean;
  role?: Role;
  sortBy?: 'firstName' | 'lastName' | 'email' | 'createdAt';
  sortOrder?: 'asc' | 'desc';
}

export interface UserLinkedSite {
  id: string;
  code: string;
  name: string;
  isLinked: boolean;
  isDefault: boolean;
}

export interface UpdateUserSitesDto {
  siteIds: string[];
  defaultSiteId?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

// ============================================================================
// API FUNCTIONS
// ============================================================================

export const usersApi = {
  /**
   * Crear un nuevo usuario
   */
  create: async (data: CreateUserDto): Promise<User> => {
    return api.post<User>('/users', data);
  },

  /**
   * Obtener todos los usuarios (paginado)
   */
  getAll: async (params?: UserQueryParams): Promise<PaginatedResponse<User>> => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.search) searchParams.append('search', params.search);
    if (params?.includeInactive) searchParams.append('includeInactive', 'true');
    if (params?.role) searchParams.append('role', params.role);
    if (params?.sortBy) searchParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) searchParams.append('sortOrder', params.sortOrder);
    
    const query = searchParams.toString();
    return api.get<PaginatedResponse<User>>(`/users${query ? `?${query}` : ''}`);
  },

  /**
   * Obtener usuarios para dropdown
   */
  getForSelect: async (): Promise<UserSelect[]> => {
    return api.get<UserSelect[]>('/users/select');
  },

  /**
   * Obtener un usuario por ID
   */
  getById: async (id: string): Promise<User> => {
    return api.get<User>(`/users/${id}`);
  },

  /**
   * Actualizar un usuario
   */
  update: async (id: string, data: UpdateUserDto): Promise<User> => {
    return api.patch<User>(`/users/${id}`, data);
  },

  /**
   * Desactivar un usuario
   */
  deactivate: async (id: string): Promise<void> => {
    await api.delete(`/users/${id}`);
  },

  /**
   * Reactivar un usuario
   */
  reactivate: async (id: string): Promise<void> => {
    await api.patch(`/users/${id}/reactivate`);
  },

  /**
   * Resetear contraseña (genera una temporal)
   */
  resetPassword: async (id: string): Promise<{ tempPassword: string }> => {
    return api.post<{ tempPassword: string }>(`/users/${id}/reset-password`);
  },

  /**
   * Cambiar contraseña
   */
  changePassword: async (id: string, data: ChangePasswordDto): Promise<void> => {
    await api.patch(`/users/${id}/password`, data);
  },

  /**
   * Obtener sedes vinculadas a un usuario
   */
  getLinkedSites: async (id: string): Promise<UserLinkedSite[]> => {
    return api.get<UserLinkedSite[]>(`/users/${id}/sites`);
  },

  /**
   * Actualizar sedes de un usuario
   */
  updateSites: async (
    id: string,
    data: UpdateUserSitesDto
  ): Promise<{ success: boolean; message: string }> => {
    return api.patch<{ success: boolean; message: string }>(
      `/users/${id}/sites`,
      data
    );
  },
};

export default usersApi;
